import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiAlertTriangle, FiUserX } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { isEmailValid } from "../../utils/validationUtils";
import { handleAuthError } from "../../utils/errorUtils";
import {
  AuthLayout,
  AuthInput,
  AuthButton,
  PasswordInput,
  SocialAuthButton,
} from "./components";
import styles from "./Auth.module.css";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, googleLogin } = useUserContext();
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showAccountDeleted, setShowAccountDeleted] = useState(false);

  // Get the redirect path if any
  const from = location.state?.from || "/";

  // Check if redirected due to session expiration or account deletion
  useEffect(() => {
    if (location.state?.sessionExpired) {
      setShowSessionExpired(true);
    }
    if (searchParams.get("accountDeleted") === "true") {
      setShowAccountDeleted(true);
    }
  }, [location.state, searchParams]);

  // Check for Google auth in progress
  useEffect(() => {
    if (window.location.search.includes("token=")) {
      setIsGoogleLoading(true);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError("");
    if (showSessionExpired) setShowSessionExpired(false);
    if (showAccountDeleted) setShowAccountDeleted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Please enter both email and password");
      return;
    }

    if (!isEmailValid(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await login(formData);
      navigate(from);
    } catch (err) {
      const processedError = handleAuthError(err);

      if (processedError.type === "EMAIL_NOT_VERIFIED") {
        navigate("/email-verification", {
          state: { email: formData.email, from },
        });
        return;
      }

      setError(processedError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = (e) => {
    e.preventDefault();
    setIsGoogleLoading(true);
    googleLogin();
  };

  // Google loading state
  if (isGoogleLoading) {
    return (
      <AuthLayout
        title="Signing in with Google"
        subtitle="Please wait while we authenticate you..."
      >
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <AuthButton loading fullWidth>
            Connecting...
          </AuthButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome Back"
      subtitle="Sign in to your account"
      footer={
        <>
          Don't have an account?{" "}
          <Link to="/signup" state={{ from }} className={styles['au-link']}>
            Sign up
          </Link>
        </>
      }
    >
      {/* Alert Messages */}
      <AnimatePresence>
        {showSessionExpired && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--warning']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <span>Your session has expired. Please sign in again.</span>
          </motion.div>
        )}

        {showAccountDeleted && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiUserX className={styles['au-alert__icon']} />
            <span>Your account is no longer active. Please contact support.</span>
          </motion.div>
        )}

        {error && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sign In Form */}
      <form onSubmit={handleSubmit} className={styles['au-form']}>
        <AuthInput
          icon={FiMail}
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          required
          disabled={isLoading}
        />

        <PasswordInput
          label="Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Enter your password"
          required
          disabled={isLoading}
        />

        <div className={styles['au-form-options']}>
          <label className={styles['au-checkbox']}>
            <input
              type="checkbox"
              className={styles['au-checkbox__input']}
              disabled={isLoading}
            />
            <span className={styles['au-checkbox__label']}>Remember me</span>
          </label>
          <Link to="/forgot-password" className={styles['au-link']}>
            Forgot password?
          </Link>
        </div>

        <AuthButton
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
        >
          {isLoading ? "Signing In..." : "Sign In"}
        </AuthButton>

        <div className={styles['au-divider']}>
          <span className={styles['au-divider__text']}>or</span>
        </div>

        <SocialAuthButton
          provider="google"
          onClick={handleGoogleSignIn}
          loading={isGoogleLoading}
          disabled={isLoading}
        />
      </form>
    </AuthLayout>
  );
};

export default SignIn;
